import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getExploreUnknownSurfaceFirstMissionPreviewUrl } from './config/exploreUnknownMissionBattle';
import './ExploreUnknownForkRouteDialog.css';

/**
 * @param {object} props
 * @param {{ kind: 'skip'|'continue', destinationSurface: string, skippedSurface?: string }} props.normalPreview
 * @param {{ kind: 'skip'|'continue', destinationSurface: string }} props.alternativePreview
 * @param {string} props.normalDestinationMapName
 * @param {string} props.alternativeDestinationMapName
 * @param {number} props.altMissionCount
 * @param {number} props.altEasyMissionCount
 * @param {boolean} props.busy
 * @param {boolean} [props.isMobile]
 * @param {(choice: 'normal'|'alternative') => void} props.onChoose
 */
export default function ExploreUnknownForkRouteDialog({
  normalPreview,
  alternativePreview,
  normalDestinationMapName,
  alternativeDestinationMapName,
  altMissionCount,
  altEasyMissionCount,
  busy,
  isMobile = false,
  onChoose,
}) {
  const { t } = useTranslation();

  const normalImageUrl = useMemo(
    () => getExploreUnknownSurfaceFirstMissionPreviewUrl(normalPreview.destinationSurface),
    [normalPreview.destinationSurface]
  );
  const alternativeImageUrl = useMemo(
    () => getExploreUnknownSurfaceFirstMissionPreviewUrl(alternativePreview.destinationSurface),
    [alternativePreview.destinationSurface]
  );
  const skippedImageUrl = useMemo(() => {
    if (normalPreview.kind !== 'skip' || !normalPreview.skippedSurface) {
      return null;
    }
    return getExploreUnknownSurfaceFirstMissionPreviewUrl(normalPreview.skippedSurface);
  }, [normalPreview]);

  const normalDestinationLine = t('exploreUnknownPage.alternativePath.routeNextWorld', {
    destinationMap: normalDestinationMapName,
  });

  const alternativeDestinationLine = t('exploreUnknownPage.alternativePath.routeNextWorld', {
    destinationMap: alternativeDestinationMapName,
  });

  return (
    <div
      className={`explore-unknown-fork-route-overlay${isMobile ? ' explore-unknown-fork-route-overlay--mobile' : ''}`}
      role="dialog"
      aria-modal="true"
    >
      <div className="explore-unknown-fork-route-dialog">
        <h2>{t('exploreUnknownPage.alternativePath.forkPrompt')}</h2>
        <p className="explore-unknown-fork-route-commitment">
          {t('exploreUnknownPage.alternativePath.forkCommitment')}
        </p>
        <div className="explore-unknown-fork-route-actions">
          <button
            type="button"
            className="explore-unknown-fork-route-choice explore-unknown-fork-route-choice--normal"
            disabled={busy}
            aria-label={`${t('exploreUnknownPage.alternativePath.chooseNormal')}: ${normalDestinationLine}`}
            onClick={() => onChoose('normal')}
          >
            <span className="explore-unknown-fork-route-choice-header">
              {t('exploreUnknownPage.alternativePath.chooseNormal')}
            </span>
            <span className="explore-unknown-fork-route-choice-art">
              {skippedImageUrl ? (
                <span
                  className="explore-unknown-fork-route-choice-skipped"
                  aria-hidden="true"
                >
                  <img
                    src={skippedImageUrl}
                    alt=""
                    onError={() => {
                      throw new Error(
                        `Explore Unknown fork-route skipped preview failed to load (${skippedImageUrl})`
                      );
                    }}
                  />
                </span>
              ) : null}
              <img
                src={normalImageUrl}
                alt=""
                onError={() => {
                  throw new Error(
                    `Explore Unknown fork-route normal preview failed to load (${normalImageUrl})`
                  );
                }}
              />
            </span>
            <span className="explore-unknown-fork-route-choice-destination">{normalDestinationLine}</span>
            <span className="explore-unknown-fork-route-choice-hint">
              {t('exploreUnknownPage.alternativePath.chooseNormalHint')}
            </span>
          </button>
          <button
            type="button"
            className="explore-unknown-fork-route-choice explore-unknown-fork-route-choice--alternative"
            disabled={busy}
            aria-label={`${t('exploreUnknownPage.alternativePath.chooseAlternative')}: ${alternativeDestinationLine}`}
            onClick={() => onChoose('alternative')}
          >
            <span className="explore-unknown-fork-route-choice-header">
              {t('exploreUnknownPage.alternativePath.chooseAlternative')}
            </span>
            <span className="explore-unknown-fork-route-choice-art">
              <img
                src={alternativeImageUrl}
                alt=""
                onError={() => {
                  throw new Error(
                    `Explore Unknown fork-route alternative preview failed to load (${alternativeImageUrl})`
                  );
                }}
              />
            </span>
            <span className="explore-unknown-fork-route-choice-destination">
              {alternativeDestinationLine}
            </span>
            <span className="explore-unknown-fork-route-choice-hint">
              {t('exploreUnknownPage.alternativePath.chooseAlternativeHint')}
            </span>
            <span className="explore-unknown-fork-route-choice-variant">
              {t('exploreUnknownPage.alternativePath.runAlternativeMissionSummary', {
                missionCount: altMissionCount,
                easyCount: altEasyMissionCount,
              })}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
